// Shared Google Maps styling + category pin builder for the directory map
// (directory.js) and the directory-modal location maps (alpine-stores.js), so
// both render identically off the one source.

// Resolved design-token → value cache. A data-URI marker SVG can't inherit CSS
// custom properties, so tokens (colors + the shadow filter) are resolved once.
const tokenCache = {};

export function resolveToken(token) {
  if (!token) return null;
  if (tokenCache[token] === undefined) {
    tokenCache[token] =
      getComputedStyle(document.documentElement).getPropertyValue(token).trim() || null;
  }
  return tokenCache[token];
}

// Fallback marker color by item type when there's no hardcoded category icon.
const TYPE_COLORS = { artist: '#00C1AE', public_art: '#FF6946', organization: '#6145BA' };
export function typeColor(type) {
  return TYPE_COLORS[type] || '#3B1320';
}

// Size a hardcoded category glyph (viewBox 0 0 12 12) 1:1 centered on the circle
// at (22, 20) and paint its `currentColor` fills with `fill`.
function glyphMarkup(glyph, fill) {
  const svg = new DOMParser().parseFromString(glyph, 'image/svg+xml').querySelector('svg');
  if (!svg) return null;
  svg.setAttribute('x', '16');
  svg.setAttribute('y', '14');
  svg.setAttribute('width', '12');
  svg.setAttribute('height', '12');
  if (fill) {
    svg.querySelectorAll('[fill="currentColor"]').forEach((el) => el.setAttribute('fill', fill));
  }
  return new XMLSerializer().serializeToString(svg);
}

// A 28px circle (design-system drop shadow) with the centered category glyph, as
// a data URI. Default: light circle + brand glyph. Selected (`active`): the
// colors invert — brand circle + light glyph.
export function markerIconUrl({ glyph = null, colorToken = null, type = null, active = false } = {}) {
  const brand = resolveToken(colorToken) || typeColor(type);
  const light = resolveToken('--c-white-100');
  const circleFill = active ? brand : light;
  const glyphFill = active ? light : brand;

  const inner = glyph
    ? glyphMarkup(glyph, glyphFill)
    : `<circle cx="22" cy="20" r="5" fill="${glyphFill}"/>`;

  // --shadow-default in filter() form so it hugs the circle's alpha; resolved to
  // a literal (the img-embedded SVG can't read CSS vars).
  const shadow = resolveToken('--shadow-default-filter');
  const circleStyle = shadow ? ` style="filter: ${shadow}"` : '';

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44" fill="none">` +
    `<circle cx="22" cy="20" r="14" fill="${circleFill}"${circleStyle}/>` +
    inner +
    `</svg>`;
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

// Full google.maps icon descriptor — call after the Maps API has loaded.
export function markerIcon(opts) {
  return {
    url: markerIconUrl(opts),
    scaledSize: new google.maps.Size(44, 44),
    anchor: new google.maps.Point(22, 20),
  };
}

// Bromo brand map style (exported from the Google Maps Style editor) — muted
// lavender landscape, purple roads, and a soft-blue waterway so the colored
// category markers stay the focal point.
export function mapStyles() {
  return [
    { featureType: 'administrative.locality', elementType: 'all', stylers: [{ visibility: 'on' }] },
    { featureType: 'administrative.locality', elementType: 'geometry.fill', stylers: [{ saturation: '100' }, { lightness: '-53' }] },
    { featureType: 'administrative.neighborhood', elementType: 'labels', stylers: [{ visibility: 'on' }] },
    { featureType: 'administrative.neighborhood', elementType: 'labels.text.fill', stylers: [{ color: '#6e406a' }] },
    { featureType: 'landscape.man_made', elementType: 'geometry.fill', stylers: [{ visibility: 'on' }, { color: '#ffffff' }] },
    { featureType: 'landscape.natural', elementType: 'geometry.fill', stylers: [{ visibility: 'on' }, { color: '#f6f2f9' }, { lightness: '0' }] },
    { featureType: 'landscape.natural.landcover', elementType: 'geometry.fill', stylers: [{ visibility: 'on' }] },
    { featureType: 'landscape.natural.terrain', elementType: 'geometry.fill', stylers: [{ visibility: 'on' }] },
    { featureType: 'poi', elementType: 'geometry.fill', stylers: [{ color: '#d5f6e9' }] },
    { featureType: 'poi', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.attraction', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.attraction', elementType: 'geometry.fill', stylers: [{ color: '#ff0000' }] },
    { featureType: 'poi.business', elementType: 'all', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.business', elementType: 'geometry.fill', stylers: [{ color: '#ee9334' }] },
    { featureType: 'poi.government', elementType: 'geometry.fill', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'all', stylers: [{ visibility: 'on' }, { lightness: '-22' }, { saturation: '15' }, { color: '#b8a1cd' }] },
    { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#f3e8f7' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'labels.text', stylers: [{ visibility: 'on' }, { saturation: '0' }, { lightness: '-25' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ visibility: 'on' }, { lightness: '-82' }, { color: '#6e406a' }] },
    { featureType: 'road', elementType: 'labels.text.stroke', stylers: [{ visibility: 'on' }, { color: '#ffffff' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'road.highway', elementType: 'all', stylers: [{ saturation: '0' }, { lightness: '0' }] },
    { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ saturation: '1' }, { lightness: '11' }, { gamma: '2.60' }, { color: '#e2bbee' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ gamma: '6.13' }, { visibility: 'off' }] },
    { featureType: 'road.highway', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'all', stylers: [{ visibility: 'on' }, { saturation: '75' }, { color: '#818fe6' }, { lightness: '14' }, { gamma: '1.61' }] },
  ];
}
